import { Alert, AlertDescription, AlertTitle } from "@nimit9/signet-ui/components/alert"
import { AnswerHeadline } from "@nimit9/signet-ui/components/answer-headline"
import { Button } from "@nimit9/signet-ui/components/button"
import { EmptyState } from "@nimit9/signet-ui/components/empty-state"
import { ErrorState } from "@nimit9/signet-ui/components/error-state"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@nimit9/signet-ui/components/tabs"
import { useMediaQuery } from "@nimit9/signet-ui/hooks/use-media-query"
import type { HoldingRow } from "@paisa/types"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import { useEffect, useState } from "react"
import { Amount } from "@/components/common/amount"
import { Header } from "@/components/layout/header"
import { PageWrapper } from "@/components/layout/page-wrapper"
import { useInvestments } from "@/hooks/use-investments"
import { renderAnswerAmount, toSignetHeadline } from "@/lib/answer-headline"
import { api } from "@/lib/api"
import { useUIStore } from "@/store/ui"
import { AddFdDialog } from "./-add-fd-dialog"
import { AddSipDialog } from "./-add-sip-dialog"
import { FixedDepositsCard } from "./-fixed-deposits-card"
import { buildInvestmentsHeadline } from "./-headline"
import { HoldingCards, HoldingsTable } from "./-holdings"
import { InvestmentsLoadingSkeleton } from "./-loading-skeleton"
import { AllocationCard, TaxCard } from "./-panels"
import { SipCards, SipsEmpty, SipsTable } from "./-sips"
import { SummaryHero, SummaryStats } from "./-summary"
import { TransactionHistoryDialog } from "./-transaction-history-dialog"

export const Route = createFileRoute("/investments/")({
  component: Investments,
})

const SECTION_LABEL = "text-label uppercase tracking-wider text-text-muted"

/** Phone sub-tabs: one per holding bucket that actually has rows, then SIPs and FDs. */
function holdingTabs(holdings: HoldingRow[]) {
  const buckets = [
    { value: "stocks", label: "Stocks", rows: holdings.filter((h) => h.type === "stock") },
    { value: "funds", label: "Funds", rows: holdings.filter((h) => h.type === "mutual_fund") },
    {
      value: "other",
      label: "Other",
      rows: holdings.filter((h) => h.type !== "stock" && h.type !== "mutual_fund"),
    },
  ]
  return buckets.filter((b) => b.rows.length > 0)
}

function Investments() {
  const { data, isLoading, isError, error, refetch } = useInvestments()
  const queryClient = useQueryClient()
  const isDesktop = useMediaQuery("(min-width: 768px)")
  const [showAddSip, setShowAddSip] = useState(false)
  const [showAddFd, setShowAddFd] = useState(false)
  const [selectedHolding, setSelectedHolding] = useState<{ id: string; name: string } | null>(null)
  const [zerodhaTokenExpired, setZerodhaTokenExpired] = useState(false)

  const pendingAction = useUIStore((s) => s.pendingAction)
  const setPendingAction = useUIStore((s) => s.setPendingAction)

  useEffect(() => {
    if (pendingAction === "add_fd") {
      setShowAddFd(true)
      setPendingAction(null)
    }
  }, [pendingAction, setPendingAction])

  const syncZerodha = useMutation({
    mutationFn: async () => {
      // Use raw fetch to intercept 401 token_expired without triggering AuthError
      const res = await fetch("/api/investments/zerodha/sync", {
        method: "POST",
        credentials: "include",
      })
      if (res.status === 401) {
        const body = await res.json().catch(() => ({}))
        if (body?.code === "token_expired") {
          setZerodhaTokenExpired(true)
          throw new Error("token_expired")
        }
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body?.error || `HTTP ${res.status}`)
      }
      return res.json()
    },
    onSuccess: () => {
      setZerodhaTokenExpired(false)
      queryClient.invalidateQueries({ queryKey: ["investments"] })
    },
  })

  const reAuth = useMutation({
    mutationFn: () =>
      api.post<{ success: boolean; loginUrl: string }>("/investments/zerodha/connect", {}),
    onSuccess: (d) => {
      window.location.href = d.loginUrl
    },
  })

  if (isLoading) {
    return <InvestmentsLoadingSkeleton />
  }

  if (isError) {
    return (
      <>
        <Header title="Investments" showPeriod={false} />
        <PageWrapper>
          <ErrorState message={(error as Error)?.message} onRetry={refetch} />
        </PageWrapper>
      </>
    )
  }

  const summary = data?.summary
  const headline = buildInvestmentsHeadline(summary)
  const holdings = data?.holdings || []
  const allocation = data?.allocation || {}
  const accounts = data?.accounts || []
  const connected = accounts.find((a) => a.isConnected)
  const sips = data?.sips?.all ?? []

  if (!summary || summary.holdingsCount === 0) {
    return (
      <>
        <Header title="Investments" showPeriod={false} />
        <PageWrapper>
          <EmptyState
            title="No investments tracked"
            description="Connect Zerodha in Settings → Integrations to sync your portfolio"
            action={
              <Link
                to="/settings"
                className="rounded-lg bg-accent-green/10 px-4 py-2 text-body font-medium text-accent-green"
              >
                Go to Integrations
              </Link>
            }
          />
        </PageWrapper>
      </>
    )
  }

  const openHistory = (h: HoldingRow) => setSelectedHolding({ id: h.id, name: h.name })

  const syncButton =
    connected && !zerodhaTokenExpired ? (
      <Button
        size="sm"
        variant="outline"
        onClick={() => syncZerodha.mutate()}
        disabled={syncZerodha.isPending}
      >
        {syncZerodha.isPending ? "Syncing…" : "Sync Zerodha"}
      </Button>
    ) : undefined

  const sipHeaderMeta = (
    <>
      {data?.sips?.totalMonthly ? (
        <span className="text-body font-medium text-text-primary">
          <Amount value={data.sips.totalMonthly} />
          <span className="text-text-muted">/mo</span>
        </span>
      ) : null}
      <Button size="sm" variant="link" onClick={() => setShowAddSip(true)} className="h-auto px-0">
        + Add SIP
      </Button>
    </>
  )
  const sipTitle = `SIPs ${data?.sips?.activeSips ? `(${data.sips.activeSips} active)` : ""}`

  const tabs = holdingTabs(holdings)

  return (
    <>
      <Header
        title="Investments"
        subtitle={
          connected
            ? `Last synced: ${connected.lastSyncAt ? new Date(connected.lastSyncAt).toLocaleDateString("en-IN") : "Never"}`
            : undefined
        }
        action={syncButton}
        showPeriod={false}
      />
      <PageWrapper>
        {zerodhaTokenExpired && (
          <Alert variant="destructive" className="mb-4" data-testid="zerodha-expired-banner">
            <AlertTitle>Zerodha session expired</AlertTitle>
            <AlertDescription className="mt-2 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
              <span>Your Zerodha access token has expired. Reconnect to resume syncing.</span>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => reAuth.mutate()}
                disabled={reAuth.isPending}
                className="shrink-0"
              >
                {reAuth.isPending ? "Redirecting…" : "Reconnect Zerodha"}
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {headline && (
          <div className="mb-4">
            <AnswerHeadline
              headline={toSignetHeadline(headline)}
              renderValue={renderAnswerAmount}
            />
          </div>
        )}
        {isDesktop ? (
          <>
            <SummaryStats summary={summary} />

            <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-3">
              <div className="min-w-0 rounded-[10px] border border-border-subtle bg-bg-surface xl:col-span-2">
                <div className="border-b border-border-subtle px-4 py-3">
                  <p className={SECTION_LABEL}>Holdings ({holdings.length})</p>
                </div>
                <HoldingsTable holdings={holdings} onSelect={openHistory} />
              </div>
              <div className="min-w-0">
                <AllocationCard allocation={allocation} />
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 items-start gap-4 xl:grid-cols-3">
              <div className="min-w-0 rounded-[10px] border border-border-subtle bg-bg-surface xl:col-span-2">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border-subtle px-4 py-3">
                  <p className={SECTION_LABEL}>{sipTitle}</p>
                  <div className="flex items-center gap-3">{sipHeaderMeta}</div>
                </div>
                {sips.length > 0 ? <SipsTable sips={sips} /> : <SipsEmpty />}
              </div>
              <div className="min-w-0">
                <TaxCard tax={data?.tax} />
              </div>
            </div>

            <FixedDepositsCard onAdd={() => setShowAddFd(true)} />
          </>
        ) : (
          <div className="space-y-4">
            <SummaryHero summary={summary} />

            <Tabs defaultValue={tabs[0]?.value ?? "sips"}>
              <TabsList className="w-full">
                {tabs.map((t) => (
                  <TabsTrigger key={t.value} value={t.value}>
                    {t.label}
                  </TabsTrigger>
                ))}
                <TabsTrigger value="sips">SIPs</TabsTrigger>
                <TabsTrigger value="fds">FDs</TabsTrigger>
              </TabsList>
              {tabs.map((t) => (
                <TabsContent key={t.value} value={t.value} className="mt-2">
                  <p className={`mb-2 ${SECTION_LABEL}`}>
                    {t.label} ({t.rows.length})
                  </p>
                  <HoldingCards holdings={t.rows} onSelect={openHistory} />
                </TabsContent>
              ))}
              <TabsContent value="sips" className="mt-2">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <p className={SECTION_LABEL}>{sipTitle}</p>
                  <div className="flex items-center gap-3">{sipHeaderMeta}</div>
                </div>
                {sips.length > 0 ? <SipCards sips={sips} /> : <SipsEmpty />}
              </TabsContent>
              <TabsContent value="fds" className="mt-2">
                <FixedDepositsCard onAdd={() => setShowAddFd(true)} isDesktop={false} />
              </TabsContent>
            </Tabs>

            <AllocationCard allocation={allocation} />
            <TaxCard tax={data?.tax} />
          </div>
        )}
      </PageWrapper>

      <AddSipDialog open={showAddSip} onClose={() => setShowAddSip(false)} />
      <AddFdDialog open={showAddFd} onClose={() => setShowAddFd(false)} />
      {selectedHolding && (
        <TransactionHistoryDialog
          investmentId={selectedHolding.id}
          investmentName={selectedHolding.name}
          onClose={() => setSelectedHolding(null)}
        />
      )}
    </>
  )
}
